import axios from 'axios';
import { downloadFileFromUrl } from 'Util/_HumanWritten/FileDownloadUtils';

export interface EntityPagedResult<T> {
	items: T[];
	total: number;
}

/**
 * One page of an admin list.
 *
 * `pageNo` is **one-based**, because that is what the server means by it: `Pagination.SkipAmount` is
 * `(PageNo - 1) * PageSize`, and `Pagination.isValid()` requires `PageNo > 0`. Sending a zero-based
 * value made page one fail that validity check, so no `Skip`/`Take` was applied at all and the whole
 * table came back; page two then asked for skip 0 and returned the first page's rows again. Every page
 * after that was off by one, and the last page was unreachable.
 */
export async function fetchEntityList<T>(
	baseUrl: string,
	page = 1,
	pageSize = 100,
	/**
	 * Extra query parameters for a list endpoint that filters server-side (search terms, facet ids).
	 * Undefined and empty-array values are dropped so an inactive filter sends nothing at all.
	 * An array is repeated as `?ids=a&ids=b`, which is how ASP.NET binds a `List<T>` parameter.
	 */
	params?: Record<string, string | number | boolean | string[] | undefined>,
): Promise<EntityPagedResult<T>> {
	const query = new URLSearchParams();
	query.append('pageNo', String(page));
	query.append('pageSize', String(pageSize));

	Object.entries(params ?? {}).forEach(([key, value]) => {
		if (value === undefined || value === null || value === '') {
			return;
		}
		if (Array.isArray(value)) {
			value.forEach(entry => query.append(key, entry));
			return;
		}
		query.append(key, String(value));
	});

	const { data } = await axios.get(`${baseUrl}?${query.toString()}`);

	return {
		items: data.data ?? [],
		total: data.count ?? 0,
	};
}

export async function fetchEntityById<T>(baseUrl: string, id: string): Promise<T> {
	const { data } = await axios.get(`${baseUrl}/${id}`);
	return data;
}

export async function createEntity<T>(baseUrl: string, payload: Partial<T>): Promise<T> {
	const { data } = await axios.post(baseUrl, payload);
	return data;
}

export async function updateEntity<T extends { id?: string }>(baseUrl: string, payload: T): Promise<T> {
	const { data } = await axios.put(baseUrl, payload);
	return data;
}

export async function deleteEntity(baseUrl: string, id: string): Promise<void> {
	await axios.delete(`${baseUrl}/${id}`);
}

/**
 * Downloads the entity list as a CSV file.
 *
 * The export endpoint returns every field on the entity, not just the columns the table renders, and
 * resolves foreign keys to names server-side — so this is deliberately a download rather than a
 * client-side serialisation of the rows already on screen.
 *
 * Ignores any search term applied to the table: the search on these screens filters rows already
 * fetched, so there is nothing to send, and an export of the whole table is what is wanted anyway.
 */
export async function exportEntityList(baseUrl: string, fallbackFileName: string, query?: string): Promise<void> {
	const url = query ? `${baseUrl}/export?${query}` : `${baseUrl}/export`;

	await downloadFileFromUrl(url, fallbackFileName);
}
